import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { ActivityIndicator, Pressable, ScrollView, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

import AvatarM from "@common/components/avatar/AvatarM"
import StatusBadgeM from "@common/components/badge/StatusBadgeM"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import { ChampionImageM } from "../../student/champions/ChampionImageM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { removeDraftActivity } from "@data/redux/actions/studyMissions"
import { liveService } from "@data/services/liveService"
import { studyMissionsService } from "@data/services/studyMissionsService"
import { studentsService } from "@data/services/studentsService"
import { activitiesService } from "@data/services/activitiesService"
import RealizationsListItem from "../../student/organize/list/RealizationsListItem"
import DraftPreviewModal from "./DraftPreviewModal"
import ExistingActivityModal from "./ExistingActivityModal"
import RegenerateModal from "./RegenerateModal"
import { draftToTodoShape } from "./draftToTodoShape"

const PURPLE = theme.colors.purple ?? "#7A6EB5"
// Both section headers share one tone: colouring "À ajouter" differently made
// the two blocks look like separate widgets rather than two sections of the
// same list. Emphasis belongs to the send button, not to a label.
const SECTION_COLOR = theme.colors.greyAlt

// Strip the hydrated `material` payload from each activity before sending
// to the prescribe endpoint — the prescriber re-loads it from material_id
// and the validator doesn't expect it.
const sanitizeActivitiesForPrescribe = (activities) =>
    (activities ?? []).map(({ material, ...rest }) => rest)

// Stable React key for a proposed activity: material_id is the closest
// thing to a primary key we have client-side (a draft has no Activity id
// yet), fall back to type+idx so dropping one row doesn't shift another
// row's identity.
const activityKey = (activity, idx) =>
    `${activity?.material_id ?? "nomat"}-${activity?.type ?? "x"}-${idx}`

// Pull the skill_part positions already used by the lead activity + any
// previously-added extras, so generate-additional rotates to a different
// sub-skill instead of looping back to the same one.
const collectExcludedSkillParts = (activities) =>
    Array.from(
        new Set(
            (activities ?? [])
                .map((a) => a?.source?.skills_part_position)
                .filter((p) => p === 1 || p === 2 || p === 3)
        )
    )


// One row = [gutter][activity card][gutter]. Both blocks use the same
// three-column layout, so titles line up and the two controls (check to
// remove on the left, ⨯ to drop a proposal on the right) never land on the
// card's badges.
const ROW_LAYOUT = { flexDirection: "row", alignItems: "center" }
// Une seule reference pour « pas de TODO » : un litteral en prop ferait
// echouer la comparaison superficielle de la carte a chaque rendu.
const AUCUNE_ACTIVITE = []
const GUTTER = {
    width: 26,
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: 6,
}
// Overrides RealizationsListItem's dimensions-based width so the card fills
// the space left between the two gutters instead of bleeding off screen.
const ROW_ITEM = { flex: 1, width: "auto", alignSelf: "auto", marginBottom: 6 }

const hasPrerequisiteQuiz = (activity) =>
    !!(activity?.with_prerequisite_quiz || activity?.has_prerequisite_quiz)

// Discreet "Quizz" marker, stacked above the duration badge of a todo row.
// Same marker on both sections, so a prerequisite quiz reads the same way
// whether the activity is already in the todo or about to be added. « Quizz »
// est le mot de l'appli (onglet du TodoModal), pas « QCM ».
// Le badge partagé fait déjà icône + libellé ; on ne resserre que ses marges
// pour tenir dans la gouttière d'une ligne d'activité.
const QUIZ_BADGE = (
    <StatusBadgeM
        icon="question-circle"
        label="Quizz"
        color={theme.colors.primary}
        backgroundColor={theme.colors.primaryLighter}
        fontSize={8}
        iconSize={8}
        style={{
            alignSelf: "flex-end",
            borderRadius: 8,
            paddingHorizontal: 5,
            paddingVertical: 1,
            marginBottom: 2,
        }}
    />
)

// Header of one block inside a student card. Both blocks use it, so
// "what the student has" and "what we're adding" read as two sections of the
// same list. No icon: paired with a coloured label it read as a button, and a
// section title is not actionable.
const SectionLabel = ({ color, label, action }) => (
    <View
        style={{
            flexDirection: "row",
            alignItems: "center",
            borderTopWidth: 1,
            borderTopColor: theme.colors.lightMore,
            paddingTop: 8,
            marginBottom: 8,
        }}
    >
        <TextM
            fontSize={11}
            fontWeight="SemiBold"
            color={color}
            style={{ flex: 1 }}
        >
            {label}
        </TextM>
        {action}
    </View>
)

// Shown while the attendance fetch is still in flight. The student's name is
// already known from the store, so we print it right away instead of hiding
// the whole list behind a spinner — the tutor reads who was in the live while
// presence resolves, rather than staring at an empty screen.
const PresencePendingRow = ({ student }) => (
    <View
        style={{
            backgroundColor: theme.colors.white,
            borderRadius: 12,
            paddingVertical: 10,
            paddingHorizontal: 14,
            marginBottom: 10,
            flexDirection: "row",
            alignItems: "center",
        }}
    >
        <AvatarM
            size={32}
            user={{
                id: student.student_id,
                display_name: student.display_name,
                avatar: student.avatar,
            }}
            style={{ marginRight: 10 }}
        />
        <TextM
            fontWeight="Bold"
            fontSize={13}
            color={theme.colors.dark}
            style={{ flex: 1 }}
        >
            {student.display_name ?? student.firstname ?? "L'élève"}
        </TextM>
        <ActivityIndicator size="small" color={theme.colors.light} />
    </View>
)

// One card per student, split in two blocks because the two gestures it
// carries are independent: curating the todo the student already has
// (removal, applied on the spot) and sending what Sam proposes to add.
// "Ajouter à sa TODO" only ever ships the second block — it never touches the first.
const StudentRow = ({
    student,
    draft,
    isPrescribed,
    // « Enregistrer » sans rien de nouveau : cas courant — le tuteur a déjà
    // donné le travail pendant le live et n'ajoute rien ici. Aucun appel
    // serveur (rien à donner), la carte se replie et l'élève sort de « Reste ».
    isSavedAsIs,
    onSaveAsIs,
    onUndoSaveAsIs,
    isRegenerating,
    // Une régénération en vol, pour n'importe quel élève : le formulaire est
    // à un seul exemplaire côté écran, donc on ferme la porte plutôt que de
    // laisser le tuteur valider un geste qui sera refusé.
    isRegenerateLocked,
    isPrescribing,
    isAddingActivity,
    isUnprescribing,
    isRemovingTodos,
    removingActivityId,
    subjectColor,
    onConfirm,
    onAskRegenerate,
    onAddActivity,
    onAskUnprescribe,
    onOpenDraft,
    onOpenExistingActivity,
    existingTodoActivities = [],
    onAskRemoveActivity,
    onDropDraft,
    hasDraftError,
    onRetryDraft,
}) => {
    // Le ⨯ retire la proposition du brouillon lui-même (voir le reducer) : ce
    // que montre « À ajouter » est exactement ce qui partira, sans liste
    // parallèle à tenir à jour.
    const proposals = draft?.activities ?? []
    const studentName = student.display_name ?? student.firstname ?? "l'élève"
    // Sous 600 px (téléphone), les deux boutons du pied de carte côte à côte
    // dépassent de la carte (~445 px de libellés pour ~330 px utiles) : on les
    // empile, la primaire en pleine largeur. Même seuil que la colonne.
    const dimensions = useContext(DimensionsContext)
    const stackActions = (dimensions?.width ?? 0) < 600

    // Compact "done" layout: once a student's TODO has been updated the card
    // collapses to a 1-row summary so the screen's attention shifts to
    // whoever is still pending. Tutor can still bail out via "Annuler
    // l'ajout". The full draft is unmounted from the DOM in this state to
    // keep the list scannable.
    if (isPrescribed || isSavedAsIs) {
        // Ce que l'élève a réellement à faire maintenant : ce qu'il gardait,
        // plus ce qui vient d'être donné. « 1 exercice donné » seul mentait dès
        // que le tuteur avait conservé une ligne de « Déjà à faire ».
        const newCount = isPrescribed
            ? proposals.length || draft?.activity_ids?.length || 1
            : 0
        const total = existingTodoActivities.length + newCount
        const summary =
            total === 0
                ? "rien à faire"
                : `${total} exercice${total > 1 ? "s" : ""} à faire` +
                  (newCount > 0 && existingTodoActivities.length > 0
                      ? `, dont ${newCount} nouveau${newCount > 1 ? "x" : ""}`
                      : "")
        return (
            <View
                style={{
                    backgroundColor: theme.colors.successLight,
                    borderRadius: 12,
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    marginBottom: 8,
                    borderLeftWidth: 4,
                    borderLeftColor: theme.colors.success,
                    flexDirection: "row",
                    alignItems: "center",
                }}
            >
                <AvatarM
                    size={32}
                    user={{
                        id: student.student_id,
                        display_name: student.display_name,
                        avatar: student.avatar,
                    }}
                    style={{ marginRight: 10 }}
                />
                <FontAwesome5
                    name="check-circle"
                    size={14}
                    color={theme.colors.success}
                    solid
                    style={{ marginRight: 6 }}
                />
                <TextM
                    fontSize={13}
                    color={theme.colors.dark}
                    style={{ flex: 1 }}
                >
                    <TextM fontWeight="Bold" fontSize={13}>
                        {student.display_name ?? student.firstname}
                    </TextM>
                    {" · "}
                    {summary}
                </TextM>
                <FilledButtonM
                    mode="text"
                    // "Annuler", not "Retirer": the « Déjà à faire » block has
                    // its own red "Retirer" that deletes one activity. Two
                    // buttons with the same label doing different things is a
                    // trap. Orange stays — undoing is a caution, not a deletion.
                    label={isUnprescribing ? "…" : "Annuler"}
                    color={theme.colors.warning}
                    onPress={() =>
                        isPrescribed
                            ? onAskUnprescribe(student)
                            : onUndoSaveAsIs(student)
                    }
                    disabled={isUnprescribing}
                    loading={isUnprescribing}
                    style={{ width: null, paddingHorizontal: 6, marginBottom: 0 }}
                    contentStyle={{ height: 32 }}
                />
            </View>
        )
    }

    return (
        <View
            style={{
                backgroundColor: theme.colors.white,
                borderRadius: 12,
                padding: 14,
                marginBottom: 12,
                borderLeftWidth: 4,
                borderLeftColor: subjectColor,
                shadowColor: "#000",
                shadowOpacity: 0.05,
                shadowRadius: 4,
                shadowOffset: { width: 0, height: 2 },
                elevation: 2,
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 10,
                }}
            >
                <AvatarM
                    size={40}
                    user={{
                        id: student.student_id,
                        display_name: student.display_name,
                        avatar: student.avatar,
                    }}
                    style={{ marginRight: 10 }}
                />
                <View style={{ flex: 1 }}>
                    <TextM
                        fontWeight="Bold"
                        fontSize={15}
                        color={theme.colors.dark}
                    >
                        {student.display_name ?? student.firstname}
                    </TextM>
                    {student.grade ? (
                        <TextM fontSize={12} color={theme.colors.light}>
                            {student.grade}
                        </TextM>
                    ) : null}
                </View>
                {/* Rien ici : l'action de régénération vit au-dessus du bloc
                    « À ajouter », le seul qu'elle modifie. Dans l'en-tête, juste
                    au-dessus de la TODO, elle laissait croire qu'elle touchait
                    la TODO de l'élève (retour de Wajih). */}
            </View>

            {/* ── Déjà dans sa TODO ─────────────────────────────
                Même dessin et même geste que « À ajouter » : on ouvre une
                activité en la touchant, on la retire avec le ⨯ à sa droite.
                Un seul vocabulaire d'interaction pour les deux blocs. */}
            {existingTodoActivities.length > 0 && (
                // No bottom gap: the hairline above the next section is the
                // only separator, so the two blocks read as one continuous
                // list rather than two zones.
                <View style={{ marginBottom: 2 }}>
                    <SectionLabel
                        color={SECTION_COLOR}
                        // « À faire » est le mot de l'élève (son onglet) ; « TODO »
                        // était le nôtre, et le tuteur le répétait à l'élève.
                        label={`Déjà à faire (${existingTodoActivities.length})`}
                    />
                    {existingTodoActivities.map((activity) => (
                        <View key={`existing-${activity?.id}`} style={ROW_LAYOUT}>
                            <RealizationsListItem
                                activity={activity}
                                student={student}
                                todo
                                noOptions
                                noDragAndDrop
                                onPress={() =>
                                    onOpenExistingActivity?.(student, activity)
                                }
                                extraBadge={
                                    hasPrerequisiteQuiz(activity) ? (
                                        QUIZ_BADGE
                                    ) : null
                                }
                                hideDuration
                                style={ROW_ITEM}
                            />
                            {/* Same ⨯, same place as on a proposal — but this
                                one deletes something the student already has,
                                so it asks before. */}
                            <Pressable
                                onPress={() =>
                                    onAskRemoveActivity?.(student, activity)
                                }
                                disabled={isRemovingTodos}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                style={GUTTER}
                            >
                                {/* Seule la ligne visée tourne : le drapeau de
                                    l'élève, lui, sert à bloquer les autres ⨯ le
                                    temps de la requête, sans les faire clignoter. */}
                                {removingActivityId === activity?.id ? (
                                    <ActivityIndicator
                                        size="small"
                                        color={theme.colors.light}
                                    />
                                ) : (
                                    <FontAwesome5
                                        name="times"
                                        size={13}
                                        color={theme.colors.light}
                                    />
                                )}
                            </Pressable>
                        </View>
                    ))}
                </View>
            )}

            {!draft && hasDraftError ? (
                <View style={{ paddingVertical: 6 }}>
                    <TextM fontSize={13} color={theme.colors.warning} wrap>
                        {/* Le nom en milieu de phrase : un display_name comme
                            « Alice G. » finit déjà par un point. */}
                        La séquence de {studentName} n'a pas pu être préparée.
                    </TextM>
                    <FilledButtonM
                        mode="text"
                        label="Réessayer"
                        icon="redo"
                        color={theme.colors.primary}
                        onPress={() => onRetryDraft?.(student)}
                        style={{
                            width: null,
                            paddingHorizontal: 0,
                            marginBottom: 0,
                            alignSelf: "flex-start",
                        }}
                        labelStyle={{ fontSize: 12 }}
                        contentStyle={{ height: 28 }}
                    />
                </View>
            ) : !draft ? (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 12,
                    }}
                >
                    <ActivityIndicator
                        size="small"
                        color={PURPLE}
                        style={{ marginRight: 8 }}
                    />
                    <TextM fontSize={12} color={theme.colors.grey}>
                        Je prépare la séquence…
                    </TextM>
                </View>
            ) : (
                <>
                    {/* ── À ajouter ────────────────────────────────────
                        What "Ajouter à sa TODO" will actually add, and only that.
                        Same row component as above, so the two blocks read
                        as one single kind of object. */}
                    <SectionLabel
                        color={SECTION_COLOR}
                        label={`À ajouter (${proposals.length})`}
                        // « Recommencer » est collé au bloc qu'il remplace :
                        // c'est sa position, et non son libellé, qui dit sur quoi
                        // il agit — dans l'en-tête de la carte, juste au-dessus
                        // de la TODO, il laissait croire qu'il la touchait.
                        // Le bouton reste en place pendant la recherche, avec un
                        // libellé d'attente : le remplacer par un simple
                        // indicateur faisait disparaître l'affordance, et un
                        // état bloqué se lisait alors comme « le bouton ne fait
                        // plus rien ».
                        action={
                            <FilledButtonM
                                mode="text"
                                label={
                                    isRegenerating
                                        ? "Je recommence…"
                                        : "Recommencer"
                                }
                                icon={isRegenerating ? null : "redo"}
                                loading={isRegenerating}
                                color={theme.colors.primary}
                                onPress={() => onAskRegenerate(student)}
                                disabled={isRegenerating || isRegenerateLocked}
                                style={{
                                    width: null,
                                    paddingHorizontal: 8,
                                    marginBottom: 0,
                                    alignSelf: "center",
                                }}
                                labelStyle={{ fontSize: 11 }}
                                contentStyle={{ height: 28 }}
                            />
                        }
                    />

                    {/* Liste vide : deux causes, deux messages. Le drapeau vient
                        du reducer, seul à savoir si c'est le tuteur qui a tout
                        retiré ou le générateur qui n'a rien rendu. */}
                    {proposals.length === 0 && !draft?.emptiedByTutor ? (
                        <View style={{ paddingVertical: 6 }}>
                            <TextM
                                fontSize={13}
                                color={theme.colors.warning}
                                wrap
                            >
                                Je n'ai pas pu générer d'exercice automatique
                                (matière ou chapitre manquants).
                            </TextM>
                            <TextM
                                fontSize={12}
                                color={theme.colors.grey}
                                wrap
                                style={{ marginTop: 4 }}
                            >
                                Tu peux lui donner un exercice à la main
                                depuis le bouton + de sa liste « À faire ».
                            </TextM>
                        </View>
                    ) : proposals.length === 0 ? (
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            wrap
                            style={{ paddingVertical: 6 }}
                        >
                            Tu as retiré toutes les propositions. Recommence,
                            ajoute un exercice, ou enregistre tel quel.
                        </TextM>
                    ) : (
                        proposals.map((activity, idx) => {
                            const key = activityKey(activity, idx)
                            return (
                                <View key={key} style={ROW_LAYOUT}>
                                    <RealizationsListItem
                                        activity={draftToTodoShape(activity)}
                                        student={student}
                                        todo
                                        noOptions
                                        noDragAndDrop
                                        onPress={() => onOpenDraft?.(student, idx)}
                                        extraBadge={
                                            hasPrerequisiteQuiz(activity) ? (
                                                QUIZ_BADGE
                                            ) : null
                                        }
                                        hideDuration
                                        style={ROW_ITEM}
                                    />
                                    <Pressable
                                        onPress={() => onDropDraft?.(student, idx)}
                                        hitSlop={{
                                            top: 8,
                                            bottom: 8,
                                            left: 8,
                                            right: 8,
                                        }}
                                        style={GUTTER}
                                    >
                                        <FontAwesome5
                                            name="times"
                                            size={13}
                                            color={theme.colors.light}
                                        />
                                    </Pressable>
                                </View>
                            )
                        })
                    )}

                    {/* Pied du bloc « À ajouter » : la secondaire à gauche
                        (« Ajouter un exercice », texte), la primaire à droite.
                        Empilées et centrées, les deux se faisaient concurrence
                        et la primaire prenait toute la carte (retour de Wajih).
                        « Ajouter un exercice » reste offert même après avoir
                        tout retiré, sinon le message « ajoute-en un » n'aurait
                        aucun bouton. */}
                    <View
                        style={{
                            flexDirection: stackActions ? "column" : "row",
                            alignItems: stackActions ? "stretch" : "center",
                            justifyContent: "space-between",
                            marginTop: 4,
                        }}
                    >
                        <FilledButtonM
                            mode="text"
                            label={
                                isAddingActivity
                                    ? "Je génère un exercice…"
                                    : "Ajouter un exercice"
                            }
                            icon={isAddingActivity ? null : "plus"}
                            color={theme.colors.primary}
                            onPress={() => onAddActivity(student)}
                            loading={isAddingActivity}
                            disabled={isAddingActivity || isRegenerating}
                            style={{
                                width: null,
                                paddingHorizontal: 4,
                                marginBottom: 0,
                                alignSelf: stackActions ? "flex-start" : "auto",
                            }}
                            labelStyle={{ fontSize: 12 }}
                            contentStyle={{ height: 34 }}
                        />
                        {/* Le libellé est le geste du tuteur, avec les mots
                            qu'il dira à l'élève (« je t'ai donné deux exercices
                            à faire »). « Envoyer à X » se lisait « envoyer
                            l'élève », « Ajouter à sa TODO » parlait notre
                            jargon. */}
                        {/* Sans compte : il ne porterait que sur « À ajouter »
                            alors que « Déjà à faire » est juste au-dessus, et le
                            libellé de section le donne déjà. Sans rien à donner,
                            le bouton devient « Enregistrer » : le tuteur a déjà
                            donné le travail pendant le live, il a fini avec cet
                            élève, la carte se replie, rien ne part. */}
                        <FilledButtonM
                            label={proposals.length > 0 ? "Donner à faire" : "Enregistrer"}
                            icon="check"
                            onPress={() =>
                                proposals.length > 0
                                    ? onConfirm(student, draft)
                                    : onSaveAsIs(student)
                            }
                            loading={isPrescribing}
                            disabled={isPrescribing || isRegenerating}
                            style={{
                                width: stackActions ? "100%" : null,
                                paddingHorizontal: 12,
                                marginBottom: 0,
                                marginTop: stackActions ? 6 : 0,
                            }}
                            labelStyle={{ fontSize: 12 }}
                            contentStyle={{ height: 34 }}
                        />
                    </View>
                </>
            )}
        </View>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen
// ─────────────────────────────────────────────────────────────────────────────

const StudyMissionPrescriptionScreen = (props) => {
    const {
        navigation,
        route,
        liveSession,
        draftsByStudentId,
        failedDraftsByStudentId,
        prescriptionsByStudentId,
    } = props
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()

    const occurrenceId = route?.params?.occurrence_id
    const liveSessionId = route?.params?.liveSessionId

    // null = attendance fetch still in flight. Once loaded, this Set holds
    // the student IDs who actually connected (i.e. have a live_report with
    // statistics). We never auto-fire `generate` for absent students.
    const [connectedStudentIds, setConnectedStudentIds] = useState(null)

    useEffect(() => {
        if (!occurrenceId) return
        props.apiIdle(liveService.getLiveOccurrence)
        props.apiRequest(liveService.getLiveOccurrence, { id: occurrenceId })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [occurrenceId])

    useApiStatus(
        liveService.getLiveOccurrence,
        null,
        false,
        (successData) => {
            const ids = (successData?.live_reports ?? [])
                .filter((r) => !!r?.statistics)
                .map((r) => r?.student?.id)
                .filter(Boolean)
            setConnectedStudentIds(new Set(ids))
        },
        () => {
            // Fail open: show no one rather than the wrong list. The tutor
            // can still navigate back if needed.
            setConnectedStudentIds(new Set())
        }
    )

    const students = useMemo(() => {
        const fromParams = route?.params?.students
        // Fallback when the screen is reached without params (page reload,
        // deep link, stack restored after the live→main swap). The tutor's
        // own live_sessions expose `students`; `users` only exists on the
        // admin-side resource — reading `users` alone left the screen saying
        // "Aucun élève présent" on a live that had three.
        const fromSession =
            liveSession?.users?.filter((u) => !u?.is_tutor) ??
            liveSession?.students ??
            []
        const baseList =
            Array.isArray(fromParams) && fromParams.length > 0
                ? fromParams
                : fromSession.map((u) => ({
                      student_id: u.id,
                      display_name: u.display_name,
                      firstname: u.firstname,
                      avatar: u.avatar,
                      grade: u.grade?.name ?? u.profile?.class ?? null,
                  }))
        // `isConnected` is tri-state on purpose: null while the attendance
        // fetch is in flight, then true/false. The names come from the store
        // and are known immediately, so the list renders at once instead of
        // hiding behind a spinner — the tutor sees who was in the live while
        // presence is still resolving. Only `true` fires generate / prescribe.
        return baseList.map((s) => ({
            ...s,
            isConnected:
                connectedStudentIds === null
                    ? null
                    : connectedStudentIds.has(s.student_id),
        }))
    }, [liveSession, route?.params?.students, connectedStudentIds])

    // Index des élèves du tuteur, construit ici et non dans mapStateToProps :
    // là-bas, l'objet neuf renvoyé à chaque appel cassait la comparaison de
    // `connect`. Ces élèves portent leur `todo_activities` chargée par
    // studentsService.getStudent.
    const tutorStudentsById = useMemo(
        () =>
            (props.tutorStudents ?? []).reduce((acc, s) => {
                if (s?.id != null) acc[s.id] = s
                return acc
            }, {}),
        [props.tutorStudents]
    )

    const isLoadingAttendance = connectedStudentIds === null

    // Single-action trackers (UX limits to one regenerate / one prescribe at a time)
    const [regeneratingId, setRegeneratingId] = useState(null)
    const [prescribingId, setPrescribingId] = useState(null)
    // Après « Ajouter à sa TODO », l'écran défile jusqu'à la carte suivante
    // encore ouverte : le tuteur enchaîne les élèves sans chercher où il en
    // est. La position se MESURE au moment du défilement (`measureLayout`
    // contre le contenu du ScrollView, même idiome que
    // `useFunnelSectionScroll`), après un court délai : la carte validée
    // vient de se replier, et une position lue avant le repli viserait
    // ~270 px trop bas.
    const scrollRef = useRef(null)
    const cardRefs = useRef({})
    // Élèves « enregistrés » sans rien de nouveau : état d'écran, pas du
    // serveur — il n'y a rien à persister, la carte se replie et l'élève sort
    // de « Reste : … ». Un rechargement rouvre la carte, ce qui est juste.
    const [savedAsIsByStudentId, setSavedAsIsByStudentId] = useState({})
    const scrollToNextPending = useCallback(
        (doneStudentId) => {
            const next = students.find(
                (s) =>
                    s.isConnected &&
                    s.student_id !== doneStudentId &&
                    !prescriptionsByStudentId[s.student_id] &&
                    !savedAsIsByStudentId[s.student_id]
            )
            if (!next) return
            setTimeout(() => {
                const target = cardRefs.current[next.student_id]
                const inner = scrollRef.current?.getInnerViewNode?.()
                if (!target?.measureLayout || !inner) return
                target.measureLayout(
                    inner,
                    (_x, y) =>
                        scrollRef.current?.scrollTo({
                            y: Math.max(0, y - 12),
                            animated: true,
                        }),
                    () => {}
                )
            }, 80)
        },
        [students, prescriptionsByStudentId, savedAsIsByStudentId]
    )
    const handleSaveAsIs = useCallback(
        (student) => {
            const sid = student.student_id
            setSavedAsIsByStudentId((prev) => ({ ...prev, [sid]: true }))
            scrollToNextPending(sid)
        },
        [scrollToNextPending]
    )
    const handleUndoSaveAsIs = useCallback((student) => {
        setSavedAsIsByStudentId((prev) => {
            const next = { ...prev }
            delete next[student.student_id]
            return next
        })
    }, [])
    const [addingActivityId, setAddingActivityId] = useState(null)
    const [unprescribingId, setUnprescribingId] = useState(null)
    // Le retrait en vol, élève et ligne ensemble. Deux états séparés
    // montaient et descendaient toujours de concert, et s'en tenir à l'élève
    // faisait tourner un indicateur sur TOUTES ses lignes à la fois.
    // Un seul retrait est autorisé à la fois : cet objet est aussi le verrou.
    const [removal, setRemoval] = useState(null)
    // 🔄 Refresh icon on the student card opens GAM (stripped to the
    // Classe/Sujet/Chapitre/Niveau/Durée form) and pipes the result through
    // Sam's regenerate endpoint. Kept on the card per Wajih's preference —
    // the tutor reaches the regen gesture without having to open the
    // preview first.
    const [regenerateTarget, setRegenerateTarget] = useState(null)
    // Tapping a proposed activity opens the shared TodoModal in draft mode,
    // so a Sam proposal opens like any other activity. We track by index
    // rather than activity reference so the modal auto-updates when the
    // draft is regenerated underneath (no close/reopen flash).
    const [draftPreview, setDraftPreview] = useState(null) // { studentId, activityIndex } | null
    // Tapping an activity already in the todo opens the real TodoModal on the
    // persisted Activity — same gesture, same modal as the tutor's todo list.
    const [existingPreview, setExistingPreview] = useState(null) // { studentId, activityId } | null
    // Idempotence des deux rafales de requêtes du montage. Des refs, pas des
    // états : ces ensembles ne sont jamais lus au rendu, et les mémoriser dans
    // un `useState` coûtait deux rendus complets de l'écran (avec ses N cartes
    // et leurs lignes connectées) pour ne noter qu'un drapeau.
    const requestedDraftIds = useRef(new Set())
    // Même idempotence pour la TODO existante — on appelle /api/students/{id}
    // une fois par élève présent pour peupler
    // `currentUser.object.students[i].todo_activities`, que la carte affiche
    // dans « Déjà dans sa TODO ».
    const requestedTodoIds = useRef(new Set())

    useEffect(() => {
        if (!occurrenceId || students.length === 0) return
        students.forEach((student) => {
            if (!student.isConnected) return
            const sid = student.student_id
            if (draftsByStudentId[sid]) return
            if (requestedDraftIds.current.has(sid)) return
            requestedDraftIds.current.add(sid)
            props.apiRequest(
                studyMissionsService.generate,
                {},
                {
                    live_occurrence_id: occurrenceId,
                    student_id: sid,
                },
                // Le fan-out écrit N requêtes sur le même emplacement d'API :
                // seuls les `urlParams` reviennent avec l'échec, donc c'est là
                // qu'on porte de quoi identifier la carte à marquer. Le
                // reducer les lit ; `generate` ne les lit pas, la requête HTTP
                // est inchangée.
                { live_occurrence_id: occurrenceId, student_id: sid }
            )
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [occurrenceId, students])

    useEffect(() => {
        if (students.length === 0) return
        students.forEach((student) => {
            if (!student.isConnected) return
            const sid = student.student_id
            if (requestedTodoIds.current.has(sid)) return
            requestedTodoIds.current.add(sid)
            props.apiRequest(studentsService.getStudent, { id: sid })
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [students])

    // Surface fetch errors on the existing-todo loader — if it silently
    // fails the "Déjà dans sa to-do" section would render empty and the
    // tutor wouldn't know whether the student has no todos or whether
    // the fetch errored.
    useApiStatus(studentsService.getStudent)

    // Même garde-fou que pour la TODO juste au-dessus : si la génération
    // échoue, on le dit et on rouvre un chemin, au lieu de laisser tourner.
    // Le statut n'est là que pour libérer l'emplacement d'API ; l'élève en
    // échec, lui, est noté par le reducer — voir `failedDraftsByStudentId`.
    useApiStatus(studyMissionsService.generate, null, false, undefined, () =>
        props.apiIdle(studyMissionsService.generate)
    )

    // Réessayer passe par un appel direct : l'effet de génération ne se
    // redéclenche pas (ses dépendances n'ont pas bougé) et l'élève est déjà
    // dans `requestedDraftIds`. Le drapeau d'échec, lui, tombe tout seul
    // quand le brouillon arrive.
    const handleRetryDraft = useCallback(
        (student) => {
            const sid = student.student_id
            props.apiRequest(
                studyMissionsService.generate,
                {},
                { live_occurrence_id: occurrenceId, student_id: sid },
                { live_occurrence_id: occurrenceId, student_id: sid }
            )
        },
        [props.apiRequest, occurrenceId]
    )

    useApiStatus(
        studyMissionsService.regenerate,
        null,
        true,
        () => setRegeneratingId(null),
        () => setRegeneratingId(null)
    )

    useApiStatus(
        studyMissionsService.generateAdditional,
        null,
        true,
        () => setAddingActivityId(null),
        () => setAddingActivityId(null)
    )

    useApiStatus(
        studyMissionsService.prescribe,
        null,
        false,
        (data) => {
            setPrescribingId(null)
            scrollToNextPending(data?.student_id)
        },
        (error) => {
            setPrescribingId(null)
            const isAlreadyOpened =
                error?.code === "mission_already_opened" ||
                error?.status === 409
            DialogM.show({
                text1: isAlreadyOpened
                    ? "Mission déjà commencée"
                    : "Prescription impossible",
                text2: isAlreadyOpened
                    ? "Cet élève a déjà ouvert une activité d'une mission précédente — impossible de l'écraser."
                    : "Une erreur est survenue. Réessaie dans un instant.",
                buttonText1: "OK",
                onPress: () => {},
                exitButton: true,
                onPressBack: () => {},
            })
            // useApiStatus only auto-idles when errorMessage is truthy.
            // We render our own DialogM, so we must idle the api slot here
            // — otherwise the error stays in Redux and blocks retries.
            props.apiIdle(studyMissionsService.prescribe)
        }
    )

    useApiStatus(
        studyMissionsService.unprescribe,
        null,
        false,
        () => setUnprescribingId(null),
        (error) => {
            setUnprescribingId(null)
            const isAlreadyOpened =
                error?.code === "mission_already_opened" ||
                error?.status === 409
            DialogM.show({
                text1: isAlreadyOpened
                    ? "Trop tard pour annuler"
                    : "Annulation impossible",
                text2: isAlreadyOpened
                    ? "L'élève a déjà ouvert une activité de cette mission — on ne peut plus la retirer. Tu peux toujours en discuter directement avec lui."
                    : "Une erreur est survenue. Réessaie dans un instant.",
                buttonText1: "OK",
                onPress: () => {},
                exitButton: true,
                onPressBack: () => {},
            })
            // Same idle-on-dialog pattern as the prescribe handler above.
            props.apiIdle(studyMissionsService.unprescribe)
        }
    )

    const subjectColor =
        liveSession?.live_room?.subject?.color ?? theme.colors.primary

    // Cleaning the todo and sending new work are two separate flows. This
    // one only ever removes: it never chains into a prescribe, so a tutor
    // who tidies up without prescribing (or the other way round) gets
    // exactly what they asked for.
    //
    // Pas de rechargement de l'élève ici : le reducer `currentUser` retire
    // déjà les `deleted_activity_ids` de `students[*].todo_activities`, la
    // tranche même que cet écran lit. Le GET refaisait tout le profil
    // (~20 relations) pour relire une liste déjà juste, et la carte se vidait
    // le temps de la réponse.
    useApiStatus(
        activitiesService.deleteActivitiesTodo,
        null,
        false,
        (data) => {
            // Pas d'envoi par le canal du live, à la différence de `TodoModal` :
            // le tuteur a quitté l'appel à la finalisation. Un élève encore
            // connecté voit le retrait au prochain chargement de sa liste.
            const deletedIds = data?.deleted_activity_ids ?? []
            // Le serveur répond 200 même quand il n'a rien supprimé : une
            // activité que l'élève a déjà commencée ne se retire pas depuis
            // ici. Sans ce contrôle la ligne reste et le tuteur ne sait pas
            // pourquoi — il recommence, et ça échoue encore en silence.
            if (removal && !deletedIds.includes(removal.activityId)) {
                DialogM.show({
                    text1: "Activité non retirée",
                    text2: `« ${removal.title} » reste dans ses exercices à faire : ${removal.studentName} l'a déjà commencée, ou elle n'y est plus.`,
                    buttonText1: "OK",
                    onPress: () => {},
                    exitButton: true,
                    onPressBack: () => {},
                })
            }
            setRemoval(null)
        },
        () => {
            setRemoval(null)
            DialogM.show({
                text1: "Impossible de retirer les activités",
                text2: "Le retrait n'a pas pu être effectué. Réessaie dans un instant — ses exercices à faire sont inchangés.",
                buttonText1: "OK",
                onPress: () => {},
                exitButton: true,
                onPressBack: () => {},
            })
            props.apiIdle(activitiesService.deleteActivitiesTodo)
        }
    )

    const handleAskRemoveActivity = useCallback(
        (student, activity) => {
            if (removal) return
            const sid = student.student_id
            const id = activity?.id
            if (!id) return
            const name = student.display_name ?? student.firstname ?? "l'élève"
            const titre = activity?.material?.title ?? activity?.title ?? "cette activité"
            DialogM.show({
                text1: "Retirer cette activité ?",
                // Name first: a display_name like "Abdul K." already ends with
                // a period, so mid-sentence it produced "par Abdul K..".
                text2: `${name} ne verra plus « ${titre} ». Rien n'est ajouté à cette étape.`,
                // L'action de confirmation va en position 1 : DialogM rend ce
                // bouton EN HAUT et en plein, c'est la convention de l'appli.
                // L'avoir mis en 3 inversait l'ordre par rapport aux autres
                // dialogues (retour de Wajih).
                buttonText1: "Retirer",
                // Rouge, pas orange : on supprime ce que l'élève avait déjà.
                buttonColor1: theme.colors.error,
                onPress: () => {
                    setRemoval({
                        studentId: sid,
                        activityId: id,
                        studentName: name,
                        title: titre,
                    })
                    props.apiRequest(
                        activitiesService.deleteActivitiesTodo,
                        { student: sid },
                        { activity_ids: [id] }
                    )
                },
                buttonText3: "Annuler",
                onPress3: () => {},
                exitButton: true,
                onPressBack: () => {},
            })
        },
        [props.apiRequest, removal]
    )

    // Dropping a proposal is purely local — the draft isn't persisted yet,
    // so we just remember not to send it.
    // Le retrait sort la proposition du brouillon stocké. Il était auparavant
    // mémorisé dans une liste locale de clés : la proposition réapparaissait au
    // moindre rechargement, et le compte annoncé avant une régénération ne
    // correspondait plus à ce qui était affiché.
    const handleDropDraft = useCallback(
        (student, index) => {
            if (!occurrenceId) return
            props.removeDraftActivity({
                liveOccurrenceId: occurrenceId,
                studentId: student.student_id,
                index,
            })
        },
        [props.removeDraftActivity, occurrenceId]
    )

    // "Ajouter à sa TODO" ships the "À ajouter" list and nothing else: no archiving,
    // no implicit change to what the student already has.
    const handleConfirm = useCallback(
        (student, draft) => {
            if (prescribingId !== null) return
            const sid = student.student_id
            const activities = draft?.activities ?? []
            if (activities.length === 0) return
            setPrescribingId(sid)
            props.apiRequest(
                studyMissionsService.prescribe,
                {},
                {
                    live_occurrence_id: draft.live_occurrence_id,
                    student_id: draft.student_id,
                    subject_name: draft.subject_name,
                    nonce: draft.nonce,
                    activities: sanitizeActivitiesForPrescribe(activities),
                }
            )
        },
        [props.apiRequest, prescribingId]
    )

    const handleAskRegenerate = useCallback((student) => {
        setRegenerateTarget(student)
    }, [])

    // GAM stripped calls this with the picked options
    // ({ chapter_id?, level?, duration?, instructions? }).
    const handleRegenerateConfirm = useCallback(
        (options) => {
            const student = regenerateTarget
            // Les gardes d'abord : fermer la modale puis renoncer laissait le
            // tuteur devant un écran inchangé, sans requête et sans message.
            if (!student || !occurrenceId) return
            if (regeneratingId !== null) return
            setRegenerateTarget(null)
            // Rien à réinitialiser : la régénération remplace le brouillon, et
            // les retraits vivaient dedans.
            setRegeneratingId(student.student_id)
            props.apiRequest(
                studyMissionsService.regenerate,
                {},
                {
                    live_occurrence_id: occurrenceId,
                    student_id: student.student_id,
                    ...options,
                }
            )
        },
        [props.apiRequest, occurrenceId, regeneratingId, regenerateTarget]
    )

    // "+ Ajouter un exercice" stays a fast single-click action — no modal.
    // Chains an extra activity on the SAME chapter as the lead activity so
    // the sequence stays coherent (otherwise the tutor regenerates the lead
    // on chapter A, hits "+", and gets a B-chapter exercise back). The
    // skill_part rotation still applies via `excluded_skill_parts`.
    const handleAddActivity = useCallback(
        (student) => {
            if (!occurrenceId) return
            if (addingActivityId !== null) return
            const draft = draftsByStudentId[student.student_id]
            const excluded = collectExcludedSkillParts(draft?.activities)
            // La portée vient du brouillon lui-même : la déduire de sa
            // première activité la faisait changer dès que le tuteur
            // retirait celle-ci.
            const leadChapterId = draft?.scope?.chapter_id ?? null
            setAddingActivityId(student.student_id)
            props.apiRequest(
                studyMissionsService.generateAdditional,
                {},
                {
                    live_occurrence_id: occurrenceId,
                    student_id: student.student_id,
                    ...(excluded.length > 0
                        ? { excluded_skill_parts: excluded }
                        : {}),
                    ...(leadChapterId ? { chapter_id: leadChapterId } : {}),
                }
            )
        },
        [props.apiRequest, occurrenceId, addingActivityId, draftsByStudentId]
    )

    // Pas de confirmation : annuler l'ajout est réversible en un clic (la carte
    // revient avec ses propositions, « Ajouter à sa TODO » est à nouveau là),
    // et le seul cas irréversible — l'élève a déjà ouvert une activité — est
    // refusé par le serveur (409) et expliqué par le dialogue d'erreur dédié.
    const handleAskUnprescribe = useCallback(
        (student) => {
            if (!occurrenceId) return
            if (unprescribingId !== null) return
            setUnprescribingId(student.student_id)
            props.apiRequest(
                studyMissionsService.unprescribe,
                {},
                {
                    live_occurrence_id: occurrenceId,
                    student_id: student.student_id,
                }
            )
        },
        [props.apiRequest, occurrenceId, unprescribingId]
    )

    const goToCloseOut = useCallback(() => {
        navigation.replace("end-session-tutor", {
            occurrence_id: occurrenceId,
            liveSessionId,
            studyMissionPrescriptions: prescriptionsByStudentId,
        })
    }, [
        navigation,
        occurrenceId,
        liveSessionId,
        prescriptionsByStudentId,
    ])

    const handleContinue = useCallback(() => {
        goToCloseOut()
    }, [goToCloseOut])

    const containerWidth =
        dimensions?.width > 600
            ? 560
            : dimensions?.width
              ? dimensions.width - 32
              : "100%"

    // Counters only consider connected (attended) students — absent ones
    // show up as passive rows but aren't addressable.
    const connectedStudents = students.filter((s) => s.isConnected)
    const pendingStudents = connectedStudents.filter(
        (s) =>
            !prescriptionsByStudentId[s.student_id] &&
            !savedAsIsByStudentId[s.student_id]
    )
    const remaining = pendingStudents.length
    const pendingNames = pendingStudents
        .map((s) => s.display_name ?? s.firstname ?? "un élève")
        .join(", ")

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.greyBg,
                paddingTop: insets.top,
            }}
        >
            <ScrollView
                ref={scrollRef}
                style={{ flex: 1 }}
                contentContainerStyle={{
                    alignItems: "center",
                    paddingBottom: 24,
                    paddingHorizontal: 16,
                }}
            >
                <View style={{ width: containerWidth }}>
                    {/* En-tête compact : Sam à côté du titre, pas au-dessus.
                        Le hero empilé repoussait la première carte sous le pli
                        sur un écran que le tuteur enchaîne après chaque live. */}
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingTop: 10,
                            paddingBottom: 4,
                        }}
                    >
                        <ChampionImageM name="sam" variant="main" width={56} />
                        <View style={{ flex: 1, marginLeft: 12 }}>
                            <TextM
                                fontWeight="Bold"
                                fontSize={20}
                                color={theme.colors.primary}
                                wrap
                            >
                                Travail entre les lives
                            </TextM>
                            <TextM
                                fontSize={12}
                                color={theme.colors.light}
                                style={{ marginTop: 2 }}
                                wrap
                            >
                                Je te propose un travail personnalisé pour
                                chaque élève.
                            </TextM>
                        </View>
                    </View>

                    {students.length === 0 ? (
                        <TextM
                            color={theme.colors.grey}
                            center
                            style={{ marginTop: 32 }}
                        >
                            Aucun élève présent à ce live.
                        </TextM>
                    ) : (
                        <>
                            <View style={{ marginTop: 18, marginBottom: 10 }}>
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "baseline",
                                    }}
                                >
                                    <TextM
                                        fontWeight="Bold"
                                        fontSize={18}
                                        color={theme.colors.dark}
                                    >
                                        Pour qui ?
                                    </TextM>
                                    {/* Plus de compteur ici : le pied de page
                                        nomme qui reste, ce qui dit la même chose
                                        avec les mots du tuteur. */}
                                </View>
                            </View>
                            {students.map((student) =>
                                student.isConnected === null ? (
                                    <PresencePendingRow
                                        key={student.student_id}
                                        student={student}
                                    />
                                ) : !student.isConnected ? (
                                    <View
                                        key={student.student_id}
                                        style={{
                                            backgroundColor: theme.colors.greyBg,
                                            borderRadius: 12,
                                            paddingVertical: 10,
                                            paddingHorizontal: 14,
                                            marginBottom: 10,
                                            flexDirection: "row",
                                            alignItems: "center",
                                        }}
                                    >
                                        <AvatarM
                                            size={32}
                                            user={{
                                                id: student.student_id,
                                                display_name: student.display_name,
                                                avatar: student.avatar,
                                            }}
                                            style={{ marginRight: 10 }}
                                        />
                                        <TextM
                                            fontSize={13}
                                            color={theme.colors.grey}
                                            style={{ flex: 1 }}
                                        >
                                            <TextM
                                                fontWeight="Bold"
                                                fontSize={13}
                                                color={theme.colors.grey}
                                            >
                                                {student.display_name ??
                                                    student.firstname ??
                                                    "L'élève"}
                                            </TextM>
                                            {" est élève absent·e"}
                                        </TextM>
                                    </View>
                                ) : (
                                    <View
                                        key={student.student_id}
                                        ref={(node) => {
                                            cardRefs.current[student.student_id] = node
                                        }}
                                    >
                                    <StudentRow
                                        student={student}
                                        draft={draftsByStudentId[student.student_id]}
                                        existingTodoActivities={
                                            tutorStudentsById?.[
                                                student.student_id
                                            ]?.todo_activities ?? AUCUNE_ACTIVITE
                                        }
                                        onAskRemoveActivity={handleAskRemoveActivity}
                                        onDropDraft={handleDropDraft}
                                        hasDraftError={
                                            !!failedDraftsByStudentId[
                                                student.student_id
                                            ]
                                        }
                                        onRetryDraft={handleRetryDraft}
                                        isRemovingTodos={!!removal}
                                        removingActivityId={removal?.activityId}
                                        isPrescribed={
                                            !!prescriptionsByStudentId[
                                                student.student_id
                                            ]
                                        }
                                        isRegenerating={
                                            regeneratingId === student.student_id
                                        }
                                        isRegenerateLocked={
                                            regeneratingId !== null
                                        }
                                        isPrescribing={
                                            prescribingId === student.student_id
                                        }
                                        isAddingActivity={
                                            addingActivityId === student.student_id
                                        }
                                        isUnprescribing={
                                            unprescribingId === student.student_id
                                        }
                                        subjectColor={subjectColor}
                                        onConfirm={handleConfirm}
                                        onAskRegenerate={handleAskRegenerate}
                                        onAddActivity={handleAddActivity}
                                        onAskUnprescribe={handleAskUnprescribe}
                                        isSavedAsIs={
                                            !!savedAsIsByStudentId[student.student_id]
                                        }
                                        onSaveAsIs={handleSaveAsIs}
                                        onUndoSaveAsIs={handleUndoSaveAsIs}
                                        onOpenDraft={(s, idx) =>
                                            // L'index vient de la ligne : la
                                            // liste rendue EST celle du
                                            // brouillon, inutile de l'y
                                            // rechercher.
                                            setDraftPreview({
                                                studentId: s.student_id,
                                                activityIndex: idx,
                                            })
                                        }
                                        onOpenExistingActivity={(s, activity) =>
                                            setExistingPreview({
                                                studentId: s.student_id,
                                                activityId: activity?.id,
                                            })
                                        }
                                    />
                                    </View>
                                )
                            )}
                        </>
                    )}
                </View>
            </ScrollView>

            <View
                style={{
                    backgroundColor: theme.colors.white,
                    paddingTop: 10,
                    paddingBottom: Math.max(insets.bottom, 10),
                    paddingHorizontal: 16,
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.lightMore,
                    alignItems: "center",
                }}
            >
                {connectedStudents.length > 0 ? (
                    <TextM
                        fontSize={12}
                        color={theme.colors.grey}
                        center
                        style={{ marginBottom: 4 }}
                        wrap
                    >
                        {/* Qui reste, nommément — « 1/2 faits » comptait des
                            élèves comme des cases. */}
                        {remaining > 0
                            ? `Reste : ${pendingNames}`
                            : "Tout le monde a son travail"}
                    </TextM>
                ) : null}
                {isLoadingAttendance ? null : remaining > 0 ? (
                    <FilledButtonM
                        mode="text"
                        label="Passer cette étape"
                        color={theme.colors.grey}
                        onPress={handleContinue}
                        style={{
                            width: null,
                            marginBottom: 0,
                        }}
                        contentStyle={{ height: 36 }}
                    />
                ) : (
                    <FilledButtonM
                        label="Continuer vers le bilan"
                        icon="arrow-right"
                        onPress={handleContinue}
                        color={theme.colors.success}
                        style={{ width: containerWidth }}
                    />
                )}
            </View>

            <ExistingActivityModal
                preview={existingPreview}
                students={students}
                tutorStudentsById={tutorStudentsById}
                schoolSubjects={props.schoolSubjects}
                navigation={navigation}
                onClose={() => setExistingPreview(null)}
            />

            <DraftPreviewModal
                preview={draftPreview}
                students={students}
                draftsByStudentId={draftsByStudentId}
                schoolSubjects={props.schoolSubjects}
                navigation={navigation}
                onClose={() => setDraftPreview(null)}
            />

            <RegenerateModal
                target={regenerateTarget}
                draftsByStudentId={draftsByStudentId}
                tutorStudentsById={tutorStudentsById}
                schoolSubjects={props.schoolSubjects}
                navigation={navigation}
                onClose={() => setRegenerateTarget(null)}
                onConfirm={handleRegenerateConfirm}
            />
        </View>
    )
}

const mapStateToProps = (state, ownProps) => {
    const rawLiveSessionId =
        ownProps?.route?.params?.liveSessionId ?? state.liveMode?.liveSessionId
    const liveSessionId =
        typeof rawLiveSessionId === "string"
            ? parseInt(rawLiveSessionId, 10)
            : rawLiveSessionId
    const occurrenceId = ownProps?.route?.params?.occurrence_id
    return {
        liveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === liveSessionId
        ),
        draftsByStudentId:
            state.data.studyMissions?.draftsByOccurrenceAndStudent?.[
                occurrenceId
            ] ?? {},
        failedDraftsByStudentId:
            state.data.studyMissions?.failedDraftsByOccurrenceAndStudent?.[
                occurrenceId
            ] ?? {},
        prescriptionsByStudentId:
            state.data.studyMissions?.prescriptionsByOccurrenceAndStudent?.[
                occurrenceId
            ] ?? {},
        schoolSubjects: state.data.staticData?.school_subjects,
        // Référence brute, pas un index construit ici : un objet neuf à chaque
        // appel ferait échouer la comparaison superficielle de `connect`, et
        // l'écran se re-rendrait sur n'importe quelle action du store.
        // L'indexation se fait dans le composant, mémoïsée sur ce tableau.
        // Ces élèves portent leur `todo_activities` fraîchement chargée
        // (studentsService.getStudent → reducer currentUser), que la carte
        // affiche dans « Déjà dans sa TODO ».
        tutorStudents: state.data.currentUser?.object?.students,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    removeDraftActivity,
}

export default connect(mapStateToProps, mapDispatchToProps)(StudyMissionPrescriptionScreen)
